import { gql, useMutation } from '@apollo/client';
import type { EventVenue } from '@shady-pines/db';
import type { Uuid } from '@shady-pines/types';
import { useCallback } from 'react';
import type { MutationResponse } from '../types';
import { useSupabase } from '../use-supabase';

const EDIT_VENUE = gql`
  mutation EditVenue(
    $id: UUID!
    $name: String
    $address: String
    $city: String
    $state: String
    $postalCode: String
    $url: String
  ) {
    updateevent_venuesCollection(
      set: {
        name: $name
        address: $address
        city: $city
        state: $state
        postal_code: $postalCode
        url: $url
      }
      filter: { id: { eq: $id } }
      atMost: 1
    ) {
      records {
        __typename
        id
        name
        address
        city
        state
        postal_code
        url
      }
    }
  }
`;

export const useEditVenue = () => {
  const [editVenue, { loading, error }] = useMutation<
    MutationResponse<'updateevent_venuesCollection', EventVenue>
  >(EDIT_VENUE, {
    refetchQueries: ['VenuesQuery'],
  });

  const edit = useCallback(
    (
      id: Uuid<EventVenue>,
      input: {
        name?: string;
        address?: string;
        city?: string;
        state?: string;
        postalCode?: string;
        url?: string | null;
      },
    ) => {
      return editVenue({
        variables: {
          id,
          name: input.name,
          address: input.address,
          city: input.city,
          state: input.state,
          postalCode: input.postalCode,
          url: input.url,
        },
      });
    },
    [editVenue],
  );

  return {
    editVenue: edit,
    loading,
    error,
  };
};

const CREATE_EVENT_VENUE = gql`
  mutation CreateEventVenue(
    $name: String!
    $address: String!
    $city: String!
    $state: String!
    $postal_code: String!
    $url: String
    $createdBy: UUID!
  ) {
    insertIntoevent_venuesCollection(
      objects: [
        {
          name: $name
          address: $address
          city: $city
          state: $state
          postal_code: $postal_code
          url: $url
          created_by: $createdBy
        }
      ]
    ) {
      records {
        __typename
        id
        name
        address
        city
        state
        postal_code
        url
        created_by
      }
    }
  }
`;

export const useCreateEventVenue = () => {
  const supabase = useSupabase();
  const [createVenue, { loading, error }] =
    useMutation<MutationResponse<'insertIntoevent_venuesCollection', EventVenue>>(
      CREATE_EVENT_VENUE,
    );

  const create = useCallback(
    async (
      input: Omit<EventVenue, 'id' | 'created_by' | 'created_at' | 'updated_at' | 'deleted_at'>,
    ) => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user?.id) {
        throw new Error('User must be authenticated to create venues');
      }

      return createVenue({
        variables: {
          name: input.name,
          address: input.address,
          city: input.city,
          state: input.state,
          postal_code: input.postal_code,
          url: input.url,
          createdBy: user.id,
        },
      });
    },
    [createVenue, supabase],
  );

  return {
    createVenue: create,
    loading,
    error,
  };
};
